/**
 * Release history — mostrato nel modale "Roadmap Diakonix" cliccando
 * sulla versione in sidebar. Aggiornare qui quando esce una nuova
 * milestone; le patch release NON vanno aggiunte a meno che non siano
 * "patch storiche" con impatto significativo (es. chiusura Phase 1.5).
 *
 * Linguaggio di scrittura: user-facing, no jargon interno. Descrivi
 * l'effetto per l'utente finale, non l'implementazione tecnica.
 *
 * Manutenzione:
 *   - Al bump di una MINOR/MAJOR versione, aggiungi un'entry con
 *     `type: 'release'` (o 'milestone' se è chiusura di fase).
 *   - Al lancio di un progetto interno rilevante (Phase / Sprint /
 *     iniziativa cross-team), aggiungi un'entry `type: 'milestone'`.
 *   - Le patch (x.y.Z) NORMALMENTE non vanno in timeline (troppo
 *     dettagli per il cliente). Eccezione: patch storiche di
 *     chiusura fase, che vale la pena raccontare.
 *
 * Ordine array: cronologico DECRESCENTE (dal più recente al più
 * vecchio). Il template renderizza in quest'ordine — l'entry
 * `current` sta in cima, la `genesis` in fondo.
 */

export type ReleaseEntryType =
  | 'genesis' // punto di partenza del progetto
  | 'milestone' // chiusura di fase, rebrand, refactor major
  | 'release' // minor/major release con feature user-facing
  | 'current'; // la release attualmente attiva (highlight speciale)

export interface ReleaseEntry {
  /**
   * Data della release/milestone in formato "YYYY-MM-DD".
   * Per entry storiche imprecise si può usare solo "YYYY" — il
   * template gestisce entrambi.
   */
  date: string;
  /**
   * Numero di versione (es. "2.14.0") o etichetta libera
   * (es. "v1.x", "Genesi").
   */
  version: string;
  /** Titolo breve mostrato in grassetto (~1 frase, max 8 parole). */
  title: string;
  /**
   * Descrizione user-facing (1-3 frasi). Evita jargon tecnico.
   * Rispondi alla domanda: "cosa cambia per l'utente?".
   */
  description: string;
  /**
   * Bullet points opzionali con le principali novità della release.
   * Rendered come lista sotto la description. Utili per milestone
   * dense di feature (es. chiusura Sprint). Massimo 4 highlight.
   */
  highlights?: string[];
  /** Tipo di entry (guida la variante grafica del nodo). */
  type: ReleaseEntryType;
}

/**
 * Timeline delle release Diakonix in ordine cronologico decrescente
 * (dalla più recente → alla nascita del progetto). Il template la
 * renderizza dall'alto verso il basso; l'entry `current` sta in cima
 * ed è evidenziata con animazione.
 */
export const RELEASE_HISTORY: readonly ReleaseEntry[] = [
  {
    date: '2026-07-10',
    version: 'v2.14.2',
    title: 'Polish UX + Credits + Roadmap',
    description:
      'Rifinitura dell\'esperienza utente e nuove sezioni informative accessibili dalla sidebar. La versione che stai usando in questo momento.',
    highlights: [
      'Click sul nome utente in sidebar → apri direttamente il tuo account',
      'Click sul logo → chi ha costruito Diakonix (questo modale)',
      'Click sulla versione → la roadmap del progetto',
      'Rimozione di codice legacy risalente al 2022',
    ],
    type: 'current',
  },
  {
    date: '2026-07-10',
    version: 'v2.14.1',
    title: 'Ottimizzazione performance',
    description:
      'Alleggerimento del bundle applicativo con rimozione di dipendenze non più utilizzate. Preparazione dell\'infrastruttura per il nuovo filtro Tracciabilità, in arrivo appena il backend sarà pronto.',
    highlights: [
      '~500 KB di codice non utilizzato rimossi',
      'Specifica API pronta per la nuova ricerca faceted',
      'Pipeline di test end-to-end pronta per il rilascio',
    ],
    type: 'release',
  },
  {
    date: '2026-07-09',
    version: 'v2.14.0',
    title: 'Modalità scura ufficiale',
    description:
      'La modalità scura arriva in tutta l\'app dopo un refactor completo del sistema colori. La preferenza è salvata per utente e si ricorda a ogni login.',
    highlights: [
      'Toggle tema in Impostazioni (chiaro / scuro)',
      'Sistema di colori centralizzato per uniformità',
      'Persistenza della preferenza per utente',
      '~54 file SCSS migrati al nuovo sistema',
    ],
    type: 'release',
  },
  {
    date: '2026-07-09',
    version: 'v2.13.1',
    title: 'Patch magazzino: KPI e obiettivi',
    description:
      'Serie di correzioni sulle statistiche di magazzino: valore dei corrieri ora corretto, obiettivi giornalieri ricalcolati dinamicamente in base ai giorni lavorativi rimanenti.',
    highlights: [
      'Card corrieri con valore in € finalmente corretto',
      'Obiettivo giornaliero calcolato dinamicamente',
      'Storico ordini: link borderò riparato',
    ],
    type: 'release',
  },
  {
    date: '2026-07-01',
    version: 'v2.13.0',
    title: 'Fine Sprint 1 · Fase 2',
    description:
      'Chiusura del primo sprint della seconda fase di lavoro. Focus su riduzione dei bug ricorrenti, cleanup del codice legacy e bootstrap dell\'automazione di qualità.',
    highlights: [
      'Bug ricorrenti sulle sotto-schede: 140 → 15 casi',
      'Regola di lint automatica per prevenire regressioni',
      'Test end-to-end iniziali con Playwright',
      'Toggle tema temporaneamente rimosso in attesa del refactor',
    ],
    type: 'milestone',
  },
  {
    date: '2026-06-26',
    version: 'v2.12.4',
    title: 'Stabilizzazione avanzata',
    description:
      'Ultima patch del ciclo di stabilizzazione: fix estetici e funzionali sparsi su tutte le pagine del restyle, con focus sulle segnalazioni PM in produzione.',
    type: 'release',
  },
  {
    date: '2026-06-22',
    version: 'v2.12.1',
    title: 'Chiusura Fase 1.5 — stabilizzazione',
    description:
      'Finale del ciclo di stabilizzazione post-restyle: la Fase 1.5 chiude con una base solida su cui costruire le prossime feature.',
    highlights: [
      'Bug fix su tutte le pagine restylate',
      'Coerenza visiva completata (palette, spacing, tipografia)',
      'Preparata la base per la Fase 2',
    ],
    type: 'milestone',
  },
  {
    date: '2026-06-16',
    version: 'v2.10.0',
    title: 'Dashboard e statistiche restyled',
    description:
      'Restyle completo delle sezioni analitiche: dashboard, statistiche, tracking spedizioni e pagine principali. Palette Diakonix uniforme e componenti coerenti fra le pagine.',
    highlights: [
      'Nuova dashboard con KPI operatori magazzino',
      'Statistiche con grafici Chart.js interattivi',
      'Tracking spedizioni con mappa integrata',
    ],
    type: 'release',
  },
  {
    date: '2026-06-08',
    version: 'v2.3.0',
    title: 'Gestione account rifatta',
    description:
      'Nuova pagina Account Diakonix single-page: lista, dettaglio, creazione e modifica gestiti in un\'unica interfaccia con modali dedicati. Fine dei salti fra pagine per gestire un utente.',
    type: 'release',
  },
  {
    date: '2026-05-21',
    version: 'v2.0.0',
    title: 'Rebranding a Diakonix + restyle completo',
    description:
      'Cambio nome ufficiale da "salvatempo" a "Diakonix". Prima major release della seconda era del prodotto: nuova identità visiva, refactor architetturale profondo, base per le release future.',
    highlights: [
      'Nome ufficiale: da "salvatempo" a "Diakonix"',
      'Nuova identità visiva (logo, palette, tipografia)',
      'Refactor architetturale profondo',
    ],
    type: 'milestone',
  },
  {
    date: '2022-2026',
    version: 'v1.x',
    title: 'Prima era del prodotto',
    description:
      'Anni di produzione stabile con feature progressive: gestione ordini di ciclo attivo e passivo, tracking spedizioni, monitoring degli operatori di magazzino, integrazione con l\'app agenti e con l\'app clienti.',
    type: 'release',
  },
  {
    date: '2022',
    version: 'Genesi',
    title: 'Nasce il progetto "salvatempo"',
    description:
      'Prima versione del gestionale, sviluppata per digitalizzare il flusso di ciclo attivo e passivo del magazzino. Il nome originale "salvatempo" descrive la promessa iniziale del prodotto: automatizzare i passaggi manuali che portavano via ore agli operatori.',
    type: 'genesis',
  },
] as const;
